import React from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Globe } from "lucide-react";

export default function TranslationsAdmin() {
  const [text, setText] = React.useState("");
  const [out, setOut] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [direction, setDirection] = React.useState("de-en");
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const [bulkResult, setBulkResult] = React.useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const [src, tgt] = direction.split("-");
      const r = await api.translate({ text, source: src, target: tgt });
      setOut(r.text || "");
      toast.success("Übersetzt");
    } catch { toast.error("Fehler"); }
    finally { setBusy(false); }
  };

  const translateAll = async () => {
    if (!window.confirm("ALLE deutschen Inhalte werden ins Englische übersetzt (Seiten, Sektionen, FAQ, Glossar, News, Preise, Settings). Bestehende englische Inhalte bleiben unverändert. Fortfahren?")) return;
    setBulkBusy(true); setBulkResult(null);
    try {
      const start = await api.translateEverything();
      if (start.started === false && start.running) {
        toast.info("Übersetzung läuft bereits …");
      } else {
        toast.success("Übersetzung gestartet – läuft im Hintergrund");
      }
      // Poll status until the background job finishes
      await new Promise((resolve) => {
        const poll = async () => {
          try {
            const st = await api.translateStatus();
            if (st.stats) setBulkResult(st.stats);
            if (st.done || !st.running) {
              if (st.error) toast.error("Fehler: " + st.error);
              else toast.success("Übersetzung abgeschlossen");
              resolve();
              return;
            }
          } catch (_) { /* keep polling */ }
          setTimeout(poll, 2500);
        };
        setTimeout(poll, 1500);
      });
    } catch (e) {
      toast.error("Fehler bei Bulk-Übersetzung");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div data-testid="admin-translations">
      <div className="mb-6"><div className="brut-tag mb-3">CMS / Übersetzung</div><h1 className="brut-h2">Übersetzungs-Werkstatt</h1>
        <p className="brut-body opacity-80 mt-3 max-w-2xl">Mit einem Klick alle deutschen Inhalte ins Englische übersetzen — oder Ad-hoc-Übersetzungen ausprobieren.</p>
      </div>

      <div className="admin-card p-6 md:p-8 mb-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold mb-1">Komplette Website übersetzen</h2>
            <p className="text-sm opacity-70 max-w-xl">Übersetzt alle Seiten, Sektionen, FAQ-Einträge, Glossar-Begriffe, News-Beiträge, Preise und Settings (DE → EN). Bereits gepflegte englische Inhalte werden NICHT überschrieben.</p>
          </div>
          <button onClick={translateAll} disabled={bulkBusy} className="brut-btn shrink-0" data-testid="translate-everything-btn">
            <Globe size={16}/> {bulkBusy ? "Übersetze …" : "Alles übersetzen"}
          </button>
        </div>
        {bulkResult && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-7 gap-3 text-center">
            {[["pages","Seiten"],["sections","Sektionen"],["faq","FAQ"],["glossary","Glossar"],["news","News"],["pricing","Preise"],["settings","Settings"]].map(([k, label]) => (
              <div key={k} className="p-3 rounded-2xl" style={{ boxShadow: "var(--neu-inset-sm)" }}>
                <div className="text-2xl font-extrabold">{bulkResult[k] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider opacity-60 font-bold">{label}</div>
              </div>
            ))}
            {bulkResult.errors > 0 && <div className="col-span-full text-xs text-red-600 mt-2">{bulkResult.errors} Fehler — Details siehe Backend-Log</div>}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="brut-label">Quelltext</label>
          <textarea rows={12} className="brut-textarea" value={text} onChange={e => setText(e.target.value)} />
          <div className="mt-3 flex items-center gap-3">
            <select className="brut-select !w-auto" value={direction} onChange={e => setDirection(e.target.value)}>
              <option value="de-en">DE → EN</option><option value="en-de">EN → DE</option>
            </select>
            <button onClick={run} disabled={busy || !text.trim()} className="brut-btn !py-2 !px-3 text-xs"><Sparkles size={12}/> Übersetzen</button>
          </div>
        </div>
        <div>
          <label className="brut-label">Ergebnis</label>
          <textarea rows={12} className="brut-textarea" value={out} readOnly />
        </div>
      </div>
    </div>
  );
}
